import React, { useMemo, useState, lazy, Suspense, useEffect } from 'react';
import Cover from './components/Cover/Cover';
import PlayerControls from './components/PlayerControls/PlayerControls';
import NowPlaying from './components/NowPlaying/NowPlaying';
import Footer from './components/Footer/Footer';
import MenuButton from './components/MenuButton/MenuButton';
import { useRadioPlayer } from './hooks/useRadioPlayer';
import { useSEO } from './hooks/useSEO';
import { useIsDesktop } from './hooks/useIsDesktop';

const Background = lazy(() => import('./components/Background/Background'));
const FloatingCards = lazy(() => import('./components/FloatingCards/FloatingCards'));
const Particles = lazy(() => import('./components/Particles/Particles'));
const BottomSheet = lazy(() => import('./components/BottomSheet/BottomSheet'));
const Modal = lazy(() => import('./components/Modal/Modal'));

type ModalSection = 'about' | 'programs' | 'social' | 'listen' | null;

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeModalSection, setActiveModalSection] = useState<ModalSection>(null);
  const [shouldLoadDecorations, setShouldLoadDecorations] = useState(false);

  const radioPlayerData = useRadioPlayer();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    setActiveModalSection(null);
    setIsMenuOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    if (shouldLoadDecorations) return;

    if (radioPlayerData.hasEverPlayed) {
      setShouldLoadDecorations(true);
      return;
    }

    if ('requestIdleCallback' in window && typeof window.requestIdleCallback === 'function') {
      const idleCallbackId = window.requestIdleCallback(() => {
        setShouldLoadDecorations(true);
      }, { timeout: 2000 });
      return () => {
        if ('cancelIdleCallback' in window && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(idleCallbackId);
        }
      };
    } else {
      const timeoutId = setTimeout(() => setShouldLoadDecorations(true), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [radioPlayerData.hasEverPlayed, shouldLoadDecorations]);

  const seoParams = useMemo(() => ({
    title: radioPlayerData.currentTrack || undefined,
    artist: radioPlayerData.currentArtist || undefined,
    isPlaying: radioPlayerData.isPlaying,
  }), [radioPlayerData.currentTrack, radioPlayerData.currentArtist, radioPlayerData.isPlaying]);

  useSEO(seoParams);

  const nowPlayingProps = useMemo(() => ({
    title: radioPlayerData.currentTrack,
    artist: radioPlayerData.currentArtist,
    isMSESupported: radioPlayerData.isMSESupported,
    metadataStatus: radioPlayerData.metadataStatus,
    isPlaying: radioPlayerData.isPlaying,
    hasEverPlayed: radioPlayerData.hasEverPlayed,
  }), [
    radioPlayerData.currentTrack,
    radioPlayerData.currentArtist,
    radioPlayerData.isMSESupported,
    radioPlayerData.metadataStatus,
    radioPlayerData.isPlaying,
    radioPlayerData.hasEverPlayed,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        document.body.classList.add('page-hidden');
      } else {
        document.body.classList.remove('page-hidden');
      }
    };

    if (document.hidden) document.body.classList.add('page-hidden');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.body.classList.remove('page-hidden');
    };
  }, []);

  return (
    <div className="app">
      {shouldLoadDecorations && (
        <>
          <Suspense fallback={null}><Background /></Suspense>
          <Suspense fallback={null}><FloatingCards /></Suspense>
        </>
      )}

      <div className="container">
        <div className={isDesktop ? 'desktop-layout' : 'mobile-layout'}>

          {!isDesktop && (
            <MenuButton
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              isOpen={isMenuOpen}
            />
          )}
          {!isDesktop && isMenuOpen && (
            <Suspense fallback={null}>
              <BottomSheet isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
            </Suspense>
          )}

          {isDesktop ? (
            <div className="desktop-main-content">
              <div className="desktop-cover-section">
                <Cover useH1={true} />
              </div>
              <div className="desktop-play-button-section">
                <PlayerControls variant="desktop" radioPlayerData={radioPlayerData} />
              </div>
              <div className="desktop-now-playing-section">
                <NowPlaying {...nowPlayingProps} />
              </div>
            </div>
          ) : (
            <>
              <div className="mobile-header"><Cover /></div>
              <div className="mobile-content">
                <PlayerControls variant="mobile" radioPlayerData={radioPlayerData} />
              </div>
              <div className="mobile-now-playing">
                <NowPlaying {...nowPlayingProps} />
              </div>
            </>
          )}

          {shouldLoadDecorations && (
            <Suspense fallback={null}><Particles /></Suspense>
          )}

          {isDesktop && activeModalSection !== null && (
            <Suspense fallback={null}>
              <Modal
                isOpen={activeModalSection !== null}
                onClose={() => setActiveModalSection(null)}
                section={activeModalSection}
              />
            </Suspense>
          )}
          {isDesktop && (
            <Footer onNavigate={(item) => setActiveModalSection(item)} />
          )}

        </div>
      </div>
    </div>
  );
};

export default App;
